import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

import { useGetAvailability } from '../../api/generated/endpoints/availability/availability'
import { getListMyBookingsQueryKey } from '../../api/generated/endpoints/bookings/bookings'
import {
  getListMyOrphanedPaymentsQueryKey,
  useListMyOrphanedPayments,
  useRebookOrphanedPayment,
} from '../../api/generated/endpoints/payments/payments'
import { HourAccordion } from '../../components/HourAccordion'
import { apiMessage } from '../../lib/api-error'
import { formatUYU } from '../../lib/money'
import { isLocalDate } from '../../lib/slots'
import { addDays, dayOfMonth, formatDayLong, formatWeekday, todayAtFacility } from '../../lib/time'

/**
 * Choosing where to spend a payment that lost its slot.
 *
 * A separate screen from `/book`, for the same reason `/round/$slug` is: this
 * one has nothing to charge. There is no price to show, no credit checkbox, no
 * hand-off to a checkout — the money is already in hand and the only question
 * is when. Folding it into `/book` would mean a prop that silently changes
 * three behaviours, one of which is whether somebody gets charged twice.
 *
 * `DayStrip` is not reused for the same kind of reason: it links to `/book` by
 * construction, so its days would walk out of this flow.
 */
export const Route = createFileRoute('/_authed/payments/$paymentId/rebook')({
  validateSearch: (search: Record<string, unknown>): { date?: string } => ({
    date: isLocalDate(search.date) ? search.date : undefined,
  }),
  component: RebookPage,
})

function RebookPage() {
  const { paymentId } = Route.useParams()
  const { date } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const queryClient = useQueryClient()
  const [openHour, setOpenHour] = useState<string | null>(null)

  const orphans = useListMyOrphanedPayments()
  const day = date ?? todayAtFacility()

  const availability = useGetAvailability({ date: day }, { query: { staleTime: 15_000 } })

  const rebook = useRebookOrphanedPayment({
    mutation: {
      retry: false, // a retried POST is a second booking
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: getListMyOrphanedPaymentsQueryKey() }),
          queryClient.invalidateQueries({ queryKey: getListMyBookingsQueryKey() }),
          availability.refetch(),
        ])
        void navigate({ to: '/bookings', search: { tab: 'upcoming' } })
      },
      onError: () => {
        // Somebody may have taken it a second ago. Show what is true now
        // rather than leaving a stale grid under the error.
        void availability.refetch()
      },
    },
  })

  if (orphans.isPending) return <p className="text-slate-600">Cargando…</p>

  const orphan = orphans.data?.items.find((item) => item.id === paymentId)
  if (orphans.isError || !orphan) {
    return (
      <div className="max-w-2xl space-y-4">
        <p role="alert" className="text-red-600">
          Ese pago ya no está pendiente de resolver.
        </p>
        <Link to="/payments" className="inline-flex min-h-12 items-center text-slate-700 underline">
          Volver a los pagos
        </Link>
      </div>
    )
  }

  const horizon = availability.data?.booking_horizon_days ?? 3
  const closed = new Set(availability.data?.closed_dates ?? [])
  const today = todayAtFacility()
  const days = Array.from({ length: horizon + 1 }, (_, offset) => addDays(today, offset))

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Elegir otro horario</h1>
        <p className="mt-1 text-slate-600">
          El pago de {formatUYU(orphan.amount_uyu_pesos)} ya está hecho. No se cobra de nuevo.
        </p>
        <p className="mt-1 text-sm text-slate-600">
          Hay tiempo hasta el {formatDayLong(orphan.refund_due_at.slice(0, 10))}
          {orphan.days_left === 1 ? ' (queda 1 día)' : ` (quedan ${orphan.days_left} días)`}.
        </p>
      </div>

      {/* Edge to edge and scrollable, so the days keep their tap size on a
          narrow phone instead of shrinking to fit. Same trick as DayStrip. */}
      <div className="-mx-6 flex gap-2 overflow-x-auto px-6 pb-1">
        {days.map((option) => {
          const selected = option === day
          const shut = closed.has(option)
          return (
            <Link
              key={option}
              to="/payments/$paymentId/rebook"
              params={{ paymentId }}
              search={{ date: option }}
              aria-current={selected ? 'date' : undefined}
              className={[
                'flex min-h-16 w-16 shrink-0 flex-col items-center justify-center rounded-lg border',
                shut ? 'border-amber-300 bg-amber-50 text-amber-900' : 'border-slate-300 bg-white',
                selected ? 'border-slate-900 bg-slate-900 text-white' : '',
              ].join(' ')}
            >
              <span className="text-xs uppercase">
                {option === today ? 'Hoy' : formatWeekday(option)}
              </span>
              <span className="text-lg font-semibold tabular-nums">{dayOfMonth(option)}</span>
            </Link>
          )
        })}
      </div>

      {availability.isPending && <p className="text-slate-600">Cargando…</p>}

      {availability.data && closed.has(day) ? (
        <p
          role="status"
          className="rounded-lg border border-amber-300 bg-amber-50 p-4 text-amber-900"
        >
          El local está cerrado ese día. Hay que elegir otro.
        </p>
      ) : (
        availability.data && (
          <HourAccordion
            // Remount on a day change so the open hour re-derives rather than
            // being synced with an effect.
            key={day}
            starts={availability.data.starts}
            slotMinutes={availability.data.slot_minutes}
            openHour={openHour}
            onOpenHour={setOpenHour}
            onSelect={(start) =>
              rebook.mutate({ paymentId, data: { date: day, start, bay_id: null } })
            }
          />
        )
      )}

      {rebook.isError && (
        <p role="alert" className="text-sm text-red-600">
          {apiMessage(rebook.error, 'Ese horario se acaba de ocupar. Conviene elegir otro.')}
        </p>
      )}

      <div className="flex flex-wrap gap-4">
        <Link to="/payments" className="inline-flex min-h-12 items-center text-slate-700 underline">
          Volver a los pagos
        </Link>
      </div>
    </div>
  )
}
